import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useRouteMatch, useHistory } from 'react-router-dom';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
// @ts-ignore
import { GlobalAppStyles } from '@qover/styles';
import { RefsContext } from '@qover/contexts';
import Config from '@qover/utils/src/Config';
import {
  useDetailsPolyfill,
  useTerms,
  useDraft,
  useDraftIdRedirect,
} from '@qover/hooks';
import { EmailModalPopup } from '@qover/organisms';
import { UpdateDraftLanguageAndCountry } from '@qover/utils/src/UpdateDraftLanguageAndCountry';
import { Contract as DraftProtos } from '@qover-private/protos-web/qover/dojo/bike/v1/contract';
import { BikeLayout } from './pages/layout/BikeLayout';
import { errorHandler, termsErrorHandler } from './utils/bikeErrorFormatter';
import { pushPaymentCheckoutDesktop } from './domain/customDataLayer';
import { productService } from './services/productService';
import { priceInfoService } from './services/priceInfoServices';
import { useSteps } from './hooks/useSteps';
import { ChatStyle } from './bike.styles';
import { Routes } from './BikeRoutes';
import * as DraftFactory from './domain/factories/DraftFactory';
import { Draft } from './domain/Draft';
import type { DraftProps } from './domain/Draft';
import type { Variant } from './domain/Terms';
import { PriceInfo } from './domain/PriceInfo';
import { bikeDecorators } from './utils/bikeDecorators';
import { useRemarketing } from './hooks/useRemarketing';

type BikeProps = {
  chat: any;
  draft?: DraftProtos;
  head: React.FC;
};

function Bike(props: BikeProps) {
  useDetailsPolyfill();
  const history = useHistory();
  const match = useRouteMatch();
  const location = useLocation();
  const search = new URLSearchParams(location.search);

  const refs = useContext(RefsContext);
  const [variants, setVariants] = useState<Variant[]>([]);

  const wizard = useDraft<Draft, DraftProtos, PriceInfo>(
    DraftFactory.createFromApiResponse(props.draft),
    productService,
    new PriceInfo(),
    priceInfoService,
  );
  const { draft, priceInfo, ...BikeWizard } = wizard;

  const { completionPercentage, currentStep } = useSteps();
  const currentPath = currentStep?.path;
  useRemarketing<Draft>(currentStep, draft);
  useDraftIdRedirect(currentStep, draft?.draftId);
  const terms = useTerms(currentStep?.fetchTerms);

  useEffect(() => {
    const promises: Array<Promise<any>> = [
      productService.getVariants(refs.country),
    ];

    if (draft.risk?.originalValue && draft.risk?.type) {
      promises.push(BikeWizard.getPriceInfo());
    }

    Promise.all(promises).then(([variantsResponse]) => {
      setVariants(variantsResponse);
    });
  }, []);

  const handleStepChange = async (form: DraftProps, redirectTo: string) => {
    const response = await BikeWizard.upsertDraft(new Draft(form));

    search.set('id', response.draftId as string);
    history.push(`${match.path}/${redirectTo}?${search.toString()}`);
  };

  const onSubmit = (form: DraftProps) => {
    if (currentStep?.nextPath) {
      return handleStepChange(form, currentStep.nextPath).catch(errorHandler);
    }

    const draftForm = new Draft(form);

    // Only for summary
    return BikeWizard.prepareForPayment(draftForm, terms)
      .then(token => {
        pushPaymentCheckoutDesktop(draft, priceInfo!);
        search.set('token', token);
        history.push(`${match.path}/payment?${search.toString()}`);
      })
      .catch(termsErrorHandler)
      .catch(errorHandler);
  };

  const linkTo = (to: string) => `${match.path}/${to}?${search.toString()}`;

  const onEmailModalSubmit = (form: { email: string }) =>
    BikeWizard.updateEmail('email', form.email);

  const showEmailModal =
    !draft?.policyholder?.email &&
    !!currentPath &&
    !!draft?.draftId &&
    currentPath !== 'confirmation';
  const { head: Head } = props;

  return (
    <>
      <Head />
      <GlobalAppStyles />
      <ChatStyle />
      <EmailModalPopup
        bindToWindowObject={Config.env !== 'production'}
        draftId={draft?.draftId}
        showEmailModal={showEmailModal}
        onSubmit={onEmailModalSubmit}
      />
      <Form
        // @ts-ignore
        decorators={bikeDecorators()}
        mutators={{ ...arrayMutators }}
        onSubmit={onSubmit}
        initialValues={draft}
        validate={currentStep?.validate}
      >
        {({ handleSubmit }) => (
          <BikeLayout
            chat={props.chat}
            completionPercentage={completionPercentage}
            flowStartUrl={`${match.path}/quote`}
            rootUrl={match.path}
            step={currentStep}
            wizard={BikeWizard}
          >
            <UpdateDraftLanguageAndCountry />
            <Routes
              currentStep={currentStep}
              draft={draft}
              handleSubmit={handleSubmit}
              linkTo={linkTo}
              match={match}
              prices={priceInfo!}
              terms={terms}
              variants={variants}
              wizard={BikeWizard}
            />
          </BikeLayout>
        )}
      </Form>
    </>
  );
}

Bike.displayName = 'Bike';

export default Bike;
